import React, { useState, useMemo, useRef } from 'react';
import { AdaptiveModal } from './AdaptiveModal';
import { DaySchedule } from './DaySchedule';
import { Todo, Event } from '../types';
import { format } from 'date-fns';
import { ClockIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface TaskReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  todos: Todo[];
  selectedDate: Date;
  onTaskDrop?: (taskId: string, dropDate: Date, timeSlot?: Date) => void;
  onEventClick?: (event: Event) => void;
  onTimeBlockClick?: (startTime: Date) => void;
  onTaskClick?: (task: Todo) => void;
  onDateChange?: (date: Date) => void;
}

// Task Card Component
interface TaskCardProps {
  todo: Todo;
  onTaskClick?: (task: Todo) => void;
  onDragStart?: (e: React.DragEvent, task: Todo) => void;
  onDragEnd?: () => void;
  isDraggable?: boolean;
  isOverdue?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  todo, 
  onTaskClick, 
  onDragStart, 
  onDragEnd, 
  isDraggable = true,
  isOverdue = false 
}) => {
  const deadline = todo.deadline ? new Date(todo.deadline) : null;
  const relevantDate = deadline;

  return (
    <div
      draggable={isDraggable}
      onDragStart={isDraggable && onDragStart ? (e) => onDragStart(e, todo) : undefined}
      onDragEnd={isDraggable ? onDragEnd : undefined}
      onClick={() => onTaskClick?.(todo)}
      className={`p-3 bg-white border rounded-lg transition-shadow ${
        isOverdue ? 'border-red-400 bg-red-50' : 'border-gray-200'
      } ${
        isDraggable ? 'cursor-move hover:shadow-md' : 'cursor-pointer hover:bg-gray-50'
      }`}
    >
      <h4 className={`font-medium ${isOverdue ? 'text-red-900' : 'text-gray-900'}`}>
        {todo.title}
      </h4>
      {todo.description && (
        <p className={`text-sm mt-1 ${isOverdue ? 'text-red-700' : 'text-gray-600'}`}>
          {todo.description}
        </p>
      )}
      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
        {todo.horizon && (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            todo.horizon === 'now' ? 'bg-red-100 text-red-800' :
            todo.horizon === 'next' ? 'bg-blue-100 text-blue-800' :
            todo.horizon === 'soon' ? 'bg-yellow-100 text-yellow-800' :
            todo.horizon === 'later' ? 'bg-gray-100 text-gray-600' :
            'bg-gray-100 text-gray-500'
          }`}>
            {todo.horizon}
          </span>
        )}
        {relevantDate && (
          <span className={`flex items-center ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
            <CalendarIcon className="w-3 h-3 mr-1" />
            {format(relevantDate, 'MMM d')}
            {isOverdue && ' (Overdue)'}
          </span>
        )}
        {todo.estimated_time_minutes && (
          <span className="flex items-center">
            <ClockIcon className="w-3 h-3 mr-1" />
            {todo.estimated_time_minutes}m
          </span>
        )}
      </div>
    </div>
  );
};

export const TaskReviewModal: React.FC<TaskReviewModalProps> = ({
  isOpen,
  onClose,
  todos,
  selectedDate,
  onTaskDrop,
  onEventClick,
  onTimeBlockClick,
  onTaskClick,
  onDateChange
}) => {
  const [draggedTask, setDraggedTask] = useState<Todo | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<'day' | 'schedule' | null>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const now = new Date();
  
  // Categorize todos
  const { completedTasks, todayTasks, overdueTasks, futureTasks } = useMemo(() => {
    // Show completed tasks that were either:
    // 1. Completed on the selected date, OR
    // 2. Had a deadline on the selected date
    const completedForSelectedDate = todos.filter(todo => {
      if (todo.status !== 'completed') return false;

      // Check if had deadline on selected date
      if (todo.deadline) {
        const deadlineStr = format(new Date(todo.deadline), 'yyyy-MM-dd');
        if (deadlineStr === selectedDateStr) return true;
      }

      // Check if completed on selected date
      if (todo.completed_date) {
        const completedDateStr = format(new Date(todo.completed_date), 'yyyy-MM-dd');
        if (completedDateStr === selectedDateStr) return true;
      }

      return false;
    });
    
    const uncompleted = todos.filter(todo => todo.status !== 'completed');
    
    const today: Todo[] = [];
    const overdue: Todo[] = [];
    const future: Todo[] = [];
    
    uncompleted.forEach(todo => {
      const deadline = todo.deadline ? new Date(todo.deadline) : null;

      if (!deadline) {
        // No deadline - show tasks with horizon 'now' as today's, others as future
        if (todo.horizon === 'now') {
          today.push(todo);
        } else {
          future.push(todo);
        }
      } else {
        const dateStr = format(deadline, 'yyyy-MM-dd');
        if (dateStr === selectedDateStr) {
          today.push(todo);
        } else if (deadline < now) {
          overdue.push(todo);
        } else {
          future.push(todo);
        }
      }
    });
    
    // Sort each category
    const sortByDate = (a: Todo, b: Todo) => {
      const getDate = (todo: Todo) => {
        return todo.deadline ? new Date(todo.deadline) : new Date('9999-12-31');
      };
      return getDate(a).getTime() - getDate(b).getTime();
    };
    
    return {
      completedTasks: completedForSelectedDate.sort(sortByDate),
      todayTasks: today.sort(sortByDate),
      overdueTasks: overdue.sort(sortByDate),
      futureTasks: future.sort(sortByDate)
    };
  }, [todos, selectedDate, selectedDateStr, now]);

  const handleDragStart = (e: React.DragEvent, task: Todo) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e: React.DragEvent, target: 'day' | 'schedule') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(target);
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDropOnDay = (e: React.DragEvent) => {
    e.preventDefault();
    // Handle drop on day
    if (draggedTask && onTaskDrop) {
      // Call onTaskDrop for day drop
      onTaskDrop(draggedTask.id, selectedDate);
    }
    setDragOverTarget(null);
  };

  const handleDropOnSchedule = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedTask || !onTaskDrop) {
      setDragOverTarget(null);
      return;
    }
    
    // Find the DaySchedule's scroll container
    const scheduleContainer = scheduleRef.current?.querySelector('.overflow-y-auto');
    if (!scheduleContainer) {
      // No schedule container found, dropping on day only
      onTaskDrop(draggedTask.id, selectedDate);
      setDragOverTarget(null);
      return;
    }
    
    // Calculate which time slot was dropped on based on mouse position
    const rect = scheduleContainer.getBoundingClientRect();
    const scrollTop = scheduleContainer.scrollTop;
    const relativeY = e.clientY - rect.top + scrollTop;
    
    // Each hour block is 80px high, account for the header
    const hourIndex = Math.floor(relativeY / 80);
    const hour = Math.max(0, Math.min(23, hourIndex)); // Clamp between 0-23
    
    // Create a date for the specific hour
    const timeSlot = new Date(selectedDate);
    timeSlot.setHours(hour, 0, 0, 0);
    
    // Drop at calculated time
    onTaskDrop(draggedTask.id, selectedDate, timeSlot);
    setDragOverTarget(null);
  };

  return (
    <AdaptiveModal isOpen={isOpen} onClose={onClose} title="Review and Schedule Tasks" size="xl">
      <div className="flex h-[80vh]">
        {/* Left side - Task list */}
        <div className="w-1/3 pr-4 border-r border-gray-200 overflow-y-auto">
          {/* Completed Section */}
          {completedTasks.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">
                Completed {format(selectedDate, 'MMM d') === format(new Date(), 'MMM d') ? 'Today' : format(selectedDate, 'MMM d')}
              </h3>
              <div className="space-y-2 opacity-60">
                {completedTasks.map(todo => (
                  <TaskCard key={todo.id} todo={todo} onTaskClick={onTaskClick} isDraggable={false} />
                ))}
              </div>
            </div>
          )}

          {/* Today's Tasks Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Today's Tasks</h3>
            <div className="space-y-2">
              {overdueTasks.length > 0 && (
                <>
                  <p className="text-sm text-red-600 font-medium mb-2">Overdue</p>
                  {overdueTasks.map(todo => (
                    <TaskCard 
                      key={todo.id} 
                      todo={todo} 
                      onTaskClick={onTaskClick} 
                      onDragStart={handleDragStart} 
                      onDragEnd={handleDragEnd} 
                      isOverdue 
                    />
                  ))}
                </>
              )}
              {todayTasks.map(todo => (
                <TaskCard 
                  key={todo.id} 
                  todo={todo} 
                  onTaskClick={onTaskClick} 
                  onDragStart={handleDragStart} 
                  onDragEnd={handleDragEnd} 
                />
              ))}
              {todayTasks.length === 0 && overdueTasks.length === 0 && (
                <p className="text-sm text-gray-500 italic">No tasks scheduled for today</p>
              )}
            </div>
          </div>

          {/* Future Tasks Section */}
          {futureTasks.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">Future Tasks</h3>
              <div className="space-y-2">
                {futureTasks.map(todo => (
                  <TaskCard 
                    key={todo.id} 
                    todo={todo} 
                    onTaskClick={onTaskClick} 
                    onDragStart={handleDragStart} 
                    onDragEnd={handleDragEnd} 
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right side - Day view */}
        <div className="w-2/3 pl-4 flex flex-col">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </h3>
              {/* Date Navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const prevDate = new Date(selectedDate);
                    prevDate.setDate(prevDate.getDate() - 1);
                    onDateChange?.(prevDate);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onDateChange?.(new Date())}
                  className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    const nextDate = new Date(selectedDate);
                    nextDate.setDate(nextDate.getDate() + 1);
                    onDateChange?.(nextDate);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Drop zone for adding to day */}
            <div
              onDragOver={(e) => handleDragOver(e, 'day')}
              onDragLeave={handleDragLeave}
              onDrop={handleDropOnDay}
              className={`p-4 border-2 border-dashed rounded-lg text-center transition-colors ${
                dragOverTarget === 'day' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 bg-gray-50'
              }`}
            >
              <p className="text-sm text-gray-600">
                Drop here to add task to {format(selectedDate, 'EEEE')}
              </p>
            </div>
          </div>

          {/* Day Schedule */}
          <div 
            ref={scheduleRef}
            className="flex-1"
            onDragOver={(e) => handleDragOver(e, 'schedule')}
            onDragLeave={handleDragLeave}
            onDrop={handleDropOnSchedule}
          >
            <div className={`h-full rounded-lg transition-all ${
              dragOverTarget === 'schedule' ? 'ring-2 ring-blue-500' : ''
            }`}>
              <DaySchedule
                date={selectedDate}
                onEventClick={onEventClick}
                onTimeBlockClick={(time) => {
                  // Don't handle time block clicks while dragging
                  if (!draggedTask && onTimeBlockClick) {
                    onTimeBlockClick(time);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </AdaptiveModal>
  );
};